// components/MouvementFinancier/components/DocumentViewerModal.jsx
import { useState, useEffect } from 'react';
import { Modal, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaFilePdf, FaFileImage, FaFile, FaDownload, FaExternalLinkAlt } from 'react-icons/fa';
import AccountHistoricDocumentService from '../../../service/api/Documents/AccountHistoricDocumentService';
import { formatDate } from '../../../service/utils/formatters';


const DocumentViewerModal = ({ 
  show, 
  onHide, 
  historicId, 
  domainId,
  accountHistoric // Optionnel: pour afficher les infos de base
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    if (show && historicId && domainId) {
      fetchDocuments();
    }
  }, [show, historicId, domainId]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await AccountHistoricDocumentService.getAccountHistoricDocuments(
        historicId, 
        domainId
      );
      
      setDocuments(data.documents || []);
    } catch (err) {
      console.error('Erreur chargement documents:', err);
      setError(err.response?.data?.message || 'Erreur lors du chargement du document');
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'PDF': return <FaFilePdf className="text-danger" size={20} />;
      case 'IMAGE': return <FaFileImage className="text-success" size={20} />;
      default: return <FaFile className="text-secondary" size={20} />;
    }
  };

  const getContainerBadge = (container) => {
    if (container === 'documents') {
      return <Badge bg="primary" className="ms-2">Cash</Badge>;
    } else if (container === 'transaction-preuves') {
      return <Badge bg="info" className="ms-2">Transaction</Badge>;
    }
    return null;
  };

  const handleOpenDocument = (url) => {
    window.open(url, '_blank');
  };

  const handleDownloadDocument = async (url, fileName) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = fileName || 'document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Erreur téléchargement:', err);
      alert('Erreur lors du téléchargement du document');
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-solid fa-file-invoice me-2"></i>
          Documents - Historique #{historicId}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Chargement des documents...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">
            <i className="fa-solid fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        ) : documents.length === 0 ? (
          <div className="text-center py-4">
            <i className="fa-solid fa-file-circle-question text-muted fa-3x mb-3"></i>
            <h5>Aucun document disponible</h5>
            <p className="text-muted">
              Cet historique ne contient pas de document de preuve.
            </p>
          </div>
        ) : (
          <div className="document-list">
            {documents.map((doc, index) => (
              <div key={index} className="card mb-3 border">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="d-flex align-items-center">
                      {getFileIcon(doc.type)}
                      <div className="ms-3">
                        <h6 className="mb-1">{doc.name}</h6>
                        <div className="text-muted small">
                          <span>{doc.type} • </span>
                          <span>{doc.fileName}</span>
                          {getContainerBadge(doc.container)}
                        </div>
                        {accountHistoric && (
                          <div className="mt-1">
                            <small className="text-muted">
                              {formatDate(accountHistoric.date)} • {accountHistoric.categorie}
                            </small>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleOpenDocument(doc.url)}
                        title="Ouvrir dans un nouvel onglet"
                      >
                        <FaExternalLinkAlt />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => handleDownloadDocument(doc.url, doc.fileName)}
                        title="Télécharger"
                      >
                        <FaDownload />
                      </button>
                    </div>
                  </div>
                  
                  {doc.url && (
                    <div className="mt-3">
                      <div className="alert alert-info small p-2 mb-2">
                        <i className="fa-solid fa-clock me-1"></i>
                        Lien valide jusqu'à : {formatDate(doc.expiryTime)}
                      </div>
                      
                      {doc.type === 'PDF' && (
                        <div className="pdf-preview mt-2">
                          <iframe
                            src={doc.url}
                            title={doc.name}
                            width="100%"
                            height="400"
                            style={{ border: 'none', borderRadius: '4px' }}
                          />
                        </div>
                      )}
                      
                      {doc.type === 'IMAGE' && (
                        <div className="image-preview mt-2 text-center">
                          <img
                            src={doc.url}
                            alt={doc.name}
                            className="img-fluid rounded"
                            style={{ maxHeight: '300px' }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal.Body>
      
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onHide}>
          Fermer
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default DocumentViewerModal;